import {
  APP_STORAGE_PATH_PREFIX,
  appStorageKeyFromUrl,
  isPubliclyServableStorageKey,
} from "./storageContract";

export const RESPONSIVE_IMAGE_CONTRACT_VERSION = 1;
export const RESPONSIVE_IMAGE_WIDTHS = [96, 192, 320, 640, 960, 1280, 1600, 2400] as const;
export const RESPONSIVE_IMAGE_WEBP_QUALITY = 84;
export const RESPONSIVE_IMAGE_STORAGE_PREFIX = "responsive/";
export const LOCAL_RESPONSIVE_IMAGE_PATH_PREFIX = "/media/.responsive/";

export type ResponsiveImageWidth = (typeof RESPONSIVE_IMAGE_WIDTHS)[number];

export type ResponsiveImageVariant = {
  width: number;
  height: number;
  storageKey: string;
  bytes: number;
};

export type ResponsiveImageManifest = {
  version: typeof RESPONSIVE_IMAGE_CONTRACT_VERSION;
  sourceStorageKey: string;
  sourceWidth: number;
  sourceHeight: number;
  sourceFormat: "jpeg" | "png" | "webp";
  variants: ResponsiveImageVariant[];
};

export type LocalResponsiveImageRecord = {
  digest: string;
  sourceWidth: number;
  widths: number[];
};

const RESPONSIVE_RASTER_EXTENSION = /\.(?:jpe?g|png|webp)$/i;

export function isResponsiveRasterPath(value: string | null | undefined) {
  const pathname = String(value ?? "").split(/[?#]/, 1)[0];
  return RESPONSIVE_RASTER_EXTENSION.test(pathname)
    && !pathname.includes("/.responsive/")
    && !pathname.startsWith(`${APP_STORAGE_PATH_PREFIX}${RESPONSIVE_IMAGE_STORAGE_PREFIX}`);
}

export function responsiveDerivativeStorageKey(sourceStorageKey: string, width: number) {
  const key = sourceStorageKey.replace(/^\/+/, "");
  if (
    !RESPONSIVE_IMAGE_WIDTHS.includes(width as ResponsiveImageWidth)
    || !isPubliclyServableStorageKey(key)
    || !isResponsiveRasterPath(key)
    || key.includes("\\")
    || key.split("/").some(segment => !segment || segment === "." || segment === "..")
  ) {
    return null;
  }
  return `${RESPONSIVE_IMAGE_STORAGE_PREFIX}${key}/w-${width}.webp`;
}

export function responsiveManifestStorageKey(sourceStorageKey: string) {
  const derivative = responsiveDerivativeStorageKey(sourceStorageKey, RESPONSIVE_IMAGE_WIDTHS[0]);
  return derivative ? `${RESPONSIVE_IMAGE_STORAGE_PREFIX}${sourceStorageKey.replace(/^\/+/, "")}/manifest.json` : null;
}

export function withOriginalOrigin(source: string, pathname: string) {
  try {
    const parsed = new URL(source);
    parsed.pathname = pathname;
    parsed.search = "";
    parsed.hash = "";
    return parsed.toString();
  } catch {
    return pathname;
  }
}

function pathnameFor(source: string) {
  try {
    return new URL(source).pathname;
  } catch {
    return source.split(/[?#]/, 1)[0];
  }
}

function localResponsiveImageRecord(source: string): LocalResponsiveImageRecord | null {
  const pathname = pathnameFor(source);
  if (!pathname.startsWith("/media/") || pathname.startsWith(LOCAL_RESPONSIVE_IMAGE_PATH_PREFIX)) {
    return null;
  }
  const records = (globalThis as typeof globalThis & {
    __RESPONSIVE_MEDIA_INDEX__?: Record<string, LocalResponsiveImageRecord>;
  }).__RESPONSIVE_MEDIA_INDEX__;
  const record = records?.[pathname];
  if (
    !record
    || !/^[a-f0-9]{64}$/i.test(record.digest)
    || !Number.isSafeInteger(record.sourceWidth)
    || record.sourceWidth < 1
    || !Array.isArray(record.widths)
  ) return null;
  const widths = record.widths.filter(width => (
    Number.isSafeInteger(width)
    && width > 0
    && width < record.sourceWidth
  ));
  return widths.length === record.widths.length
    ? { ...record, widths: Array.from(new Set(widths)).sort((a, b) => a - b) }
    : null;
}

export function responsiveDerivativeUrl(source: string, width: number): string | null {
  if (!isResponsiveRasterPath(source)) return null;

  const storageKey = appStorageKeyFromUrl(source);
  if (storageKey) {
    // Storage candidates are authorized only by a fetched manifest.
    return null;
  }

  const pathname = pathnameFor(source);
  const record = localResponsiveImageRecord(source);
  if (!record || !record.widths.includes(width)) {
    return null;
  }
  const relativePath = pathname.slice("/media/".length);
  return withOriginalOrigin(source, `${LOCAL_RESPONSIVE_IMAGE_PATH_PREFIX}${record.digest}/${relativePath}/w-${width}.webp`);
}

function srcSet(candidates: Array<{ url: string; width: number }>) {
  const unique = new Map<number, string>();
  for (const candidate of candidates) {
    if (candidate.width > 0 && !unique.has(candidate.width)) unique.set(candidate.width, candidate.url);
  }
  return Array.from(unique.entries())
    .sort(([left], [right]) => left - right)
    .map(([width, url]) => `${url} ${width}w`)
    .join(", ") || undefined;
}

export function responsiveImageSrcSet(
  source: string | null | undefined,
  maxWidth = 1600,
  availableWidths: readonly number[] = RESPONSIVE_IMAGE_WIDTHS,
) {
  const raw = source?.trim();
  if (!raw) return undefined;
  // App Storage manifests are fetched by ResponsiveImage. This synchronous helper
  // intentionally knows only the generated local index, never guessed storage URLs.
  const local = localResponsiveImageRecord(raw);
  if (!local) return undefined;
  return srcSet(availableWidths
    .filter(width => width <= maxWidth)
    .filter(width => local.widths.includes(width))
    .map(width => {
      const url = responsiveDerivativeUrl(raw, width);
      return url ? { url, width } : null;
    })
    .filter((value): value is { url: string; width: number } => Boolean(value))
    .concat({ url: raw, width: local.sourceWidth }));
}

export function responsiveImageSrcSetFromManifest(
  source: string,
  manifest: ResponsiveImageManifest,
  maxWidth = 1600,
) {
  const sourceStorageKey = appStorageKeyFromUrl(source);
  if (!sourceStorageKey || !isResponsiveImageManifest(manifest, sourceStorageKey)) return undefined;
  return srcSet(
    manifest.variants
      .filter(variant => variant.width <= maxWidth)
      .map(variant => ({
        url: withOriginalOrigin(source, `${APP_STORAGE_PATH_PREFIX}${variant.storageKey}`),
        width: variant.width,
      }))
      .concat({ url: source, width: manifest.sourceWidth }),
  );
}

export function isResponsiveImageManifest(value: unknown, sourceStorageKey: string): value is ResponsiveImageManifest {
  if (!value || typeof value !== "object") return false;
  const manifest = value as ResponsiveImageManifest;
  if (
    manifest.version !== RESPONSIVE_IMAGE_CONTRACT_VERSION
    || manifest.sourceStorageKey !== sourceStorageKey
    || !Number.isSafeInteger(manifest.sourceWidth)
    || manifest.sourceWidth < 1
    || !Array.isArray(manifest.variants)
  ) return false;
  const widths = new Set<number>();
  return manifest.variants.every(variant => (
    !!variant
    && Number.isSafeInteger(variant.width)
    && variant.width > 0
    && variant.width < manifest.sourceWidth
    && !widths.has(variant.width)
    && (widths.add(variant.width), true)
    && variant.storageKey === responsiveDerivativeStorageKey(sourceStorageKey, variant.width)
  ));
}